import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { merge } from '@oysterjs/core/graphql/loaders';
import {
  loadClient,
  loadClientActivity,
  loadClientArtifacts,
  loadClientFiles,
  loadClientNotes,
  loadClientOverview,
  loadClientWorkspaces
} from '~/api/data/clients/queries';
import { loadExcelSpreadsheetTemplates } from '~/api/data/excel-templates/queries';
import { loadCustomForms } from '~/api/data/forms/queries';
import { loadHtmlDocumentTemplates } from '~/api/data/html-templates/queries';
import { loadClientCustomPreset, loadCustomPresets } from '~/api/data/presets/queries';
import { loadCurrentUser } from '~/api/data/users/queries';
import { loadWordDocumentTemplates } from '~/api/data/word-templates/queries';
import { documentPreviewRoute } from '~/router/app/documents';
import { ClientNotesTab } from '~/pages/clients/client-notes';
import { ClientDocumentsTab } from '~/pages/clients/client-documents';
import { ClientOverviewTab, ClientPresetRunPage } from '~/pages/clients/client-overview';
import { ClientPage } from '~/pages/clients/client-page';
import { ClientUploadsTab } from '~/pages/clients/client-uploads';
import { ClientWorkspacesTab } from '~/pages/clients/client-workspaces';
import { ClientDuplicatesPage } from '~/pages/clients/client-duplicates';
import { ClientsPage } from '~/pages/clients/clients';
import { FlagGuard } from '~/components/flag-guard';
import { FEATURE_FLAGS } from '~/components/feature-flags';

export const clientsRoute: RouteObject = {
  // The list page queries through Apollo hooks, so it refetches on search and
  // sort without a route transition.
  path: 'clients',
  // Until the backfill runs these pages list nothing, so an unreleased URL reads as broken.
  element: <FlagGuard flag={FEATURE_FLAGS.SHOW_CLIENTS_TAB} to="/app/home" />,
  children: [
    { index: true, element: <ClientsPage /> },
    // Ahead of :id only for reading order -- React Router ranks the static
    // segment higher regardless, so "duplicates" is never taken for an id.
    { path: 'duplicates', element: <ClientDuplicatesPage /> },
    {
      // Each tab is a route with its own loader, so a tab opens on a loading
      // card and fades in once its first page is in the cache. The index
      // redirects so /clients/:id lands somewhere.
      path: ':id',
      loader: merge({ client: loadClient }),
      element: <ClientPage />,
      children: [
        { index: true, element: <Navigate to="overview" replace /> },
        {
          path: 'overview',
          loader: merge({
            overview: loadClientOverview,
            activity: loadClientActivity,
            agencyUser: loadCurrentUser,
            customPresets: loadCustomPresets,
            customForms: loadCustomForms,
            htmlDocumentTemplates: loadHtmlDocumentTemplates,
            wordDocumentTemplates: loadWordDocumentTemplates,
            excelSpreadsheetTemplates: loadExcelSpreadsheetTemplates
          }),
          element: <ClientOverviewTab />,
          children: [
            {
              path: 'presets/:presetId',
              // The template libraries come from the Overview's loader.
              loader: merge({ preset: loadClientCustomPreset }),
              element: <ClientPresetRunPage />
            }
          ]
        },
        {
          path: 'workspaces',
          loader: merge({ workspaces: loadClientWorkspaces }),
          element: <ClientWorkspacesTab />
        },
        {
          path: 'documents',
          loader: merge({ artifacts: loadClientArtifacts }),
          element: <ClientDocumentsTab />,
          children: [documentPreviewRoute]
        },
        {
          path: 'uploads',
          loader: merge({ files: loadClientFiles }),
          element: <ClientUploadsTab />
        },
        {
          path: 'notes',
          loader: merge({ notes: loadClientNotes }),
          element: <ClientNotesTab />
        }
      ]
    }
  ]
};
